import { useState } from "react";
import { useTranslation } from "react-i18next";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import {
  APIPaginatedRequest,
  APISuccessResponse,
  ExportFileType,
  ExportRequest,
} from "shared/api/api";
import { EntityLabel } from "shared/locale/entityLabel";

import IconButton from "features/components/IconButton";
import ConfirmationModal from "features/ui/ConfirmationModal";
import TableCount from "features/ui/TableCount";

const DEFAULT_FILE_NAME = "viaduct-download";
const DEFAULT_FILE_TYPE = "xlsx";
const LARGE_DOWNLOAD_THRESHOLD = 10000;

export interface RequestParams extends APIPaginatedRequest {
  // make limit required, otherwise download will only download default number of items on the API (25 at this point)
  limit?: number;
  // we need this for export URLs that need an ID in between, like issues/:ID/signalEvents/export/xlsx
  IDs?: string[];
  // we need this to support exporting comparison populations in issues
  includeComparisonPopulation?: boolean;
  // we need this to support separate vehicles filter on requests
  vehiclesFilter?: string;
  // event-schema exports use these to keep the file scoped to the same asset
  // population the on-screen list/count are showing.
  assetFilter?: string;
  assetType?: string;
  // we need this to track analytics for downloads
  analytics?: boolean;
}

export interface DownloadActionProps {
  requestParams: RequestParams;
  disabled?: boolean;
  fileName?: string;
  fileType?: ExportFileType;
  entityLabel: EntityLabel;
  count?: number;
  downloadFunc: (request: ExportRequest) => Promise<APISuccessResponse<Blob>>;
  downloadSource?: string;
}

const createAndDownloadFile = (
  response: APISuccessResponse<Blob>,
  fileName: string,
  fileType: ExportFileType
): void => {
  const blob = new Blob([response.data], {
    type: response.headers["content-type"] as string,
  });

  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = `${fileName}.${fileType}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const DownloadAction = ({
  requestParams,
  disabled,
  fileName = DEFAULT_FILE_NAME,
  fileType = DEFAULT_FILE_TYPE,
  entityLabel,
  count,
  downloadFunc,
  downloadSource,
}: DownloadActionProps) => {
  const { t } = useTranslation("common");
  const [isPreparingDownload, setIsPreparingDownload] = useState(false);
  const [modalText, setModalText] = useState<string | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string>();

  const handleDownloadAction = () => {
    setModalText(t("download.starting"));
    setIsPreparingDownload(true);
    setError(undefined);

    downloadFunc({
      type: fileType,
      IDs: [],
      ...requestParams,
    })
      .then((response) => {
        createAndDownloadFile(response, fileName, fileType);
        setModalOpen(false);
      })
      .catch((e) => {
        console.warn(e);
        setError(t("download.error"));
      })
      .finally(() => {
        setModalText(undefined);
        setIsPreparingDownload(false);
      });
  };

  const handleClose = (confirmed: boolean) => {
    setError(undefined);
    // we prevent closing the popup when download is in progress
    if (isPreparingDownload) return;

    if (confirmed) {
      handleDownloadAction();

      return;
    }

    setModalOpen(false);
  };

  return (
    <>
      <IconButton
        title={t("download.label")}
        onClick={(e) => {
          e.stopPropagation();
          setModalOpen(true);
        }}
        data-testid="button-download-action"
        data-download-source={downloadSource}
        disabled={disabled}
        size="small"
        color="secondary"
      >
        <FileDownloadOutlinedIcon fontSize="small" className="!text-gray-700" />
      </IconButton>
      <ConfirmationModal
        text={
          modalText ?? <DownloadInfo entityLabel={entityLabel} count={count} />
        }
        title={t("download.confirm_title")}
        isOpen={modalOpen}
        loading={isPreparingDownload}
        onClose={handleClose}
        closeDisabled={isPreparingDownload}
        error={error}
        confirmText={t("download.label")}
        closeText={t("cancel")}
      />
    </>
  );
};

interface DownloadInfoProps {
  entityLabel: EntityLabel;
  count?: number;
}

const DownloadInfo = ({ count, entityLabel }: DownloadInfoProps) => {
  const { t } = useTranslation("common");

  return (
    <div>
      {count ? (
        <div className="mb-4">
          <TableCount
            isLoading={false}
            count={count}
            error={false}
            entityLabel={entityLabel}
            extraClasses="font-bold text-gray-500 text-sm"
          />
        </div>
      ) : null}
      {Number(count) > LARGE_DOWNLOAD_THRESHOLD && (
        <p>{t("download.large_file_warning")}</p>
      )}
    </div>
  );
};

export default DownloadAction;
